import React, { useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { selectLogicMapperSettings } from '@src/features/interface/interfaceSlice';
import MinimizedPanel from '@components/MinimizedPanel/MinimizedPanel';
import { useMinimizedPanels } from './MinimizedPanelsContext';
import styles from './MinimizedPanelsContainer.module.css';

type Props = {
  /**
   * Container element to position minimized panels relative to
   */
  containerEl: HTMLDivElement | null;
}

const MINIMIZED_PANEL_GAP = 4;
const MINIMIZED_PANEL_MARGIN = 10;

/**
 * Container component that manages positioning of all minimized panels at bottom-right
 */
const MinimizedPanelsContainer = (props: Props) => {
  const { containerEl } = props;
  const settings = useSelector(selectLogicMapperSettings);
  const { ref, width, height } = useResizeDetector();
  const [panelWidths, setPanelWidths] = useState<Record<string, number>>({});
  const { getExpandHandler, getPanelTitle } = useMinimizedPanels();

  useEffect(() => {
    if (containerEl) {
      ref.current = containerEl;
    }
  }, [containerEl, ref]);

  // Get all minimized panels and sort them alphabetically by title
  const minimizedPanels = useMemo(() => {
    const panels: Array<{ id: string; title: string }> = [];

    // Check each panel type and add to array if minimized
    if (settings.portInspectorMinimized && settings.portInspectorVisible) {
      const title = getPanelTitle('portInspector');
      if (title) {
        panels.push({ id: 'portInspector', title });
      }
    }
    if (settings.globalVariablesPanelMinimized && settings.showGlobalVariablesPanel) {
      const title = getPanelTitle('globalVariables');
      if (title) {
        panels.push({ id: 'globalVariables', title });
      }
    }
    if (settings.statusPanelMinimized && settings.showStatusPanel) {
      const title = getPanelTitle('status');
      if (title) {
        panels.push({ id: 'status', title });
      }
    }

    // Sort alphabetically by title
    panels.sort((a, b) => a.title.localeCompare(b.title));

    return panels.map(panel => panel.id);
  }, [settings, getPanelTitle]);

  // Calculate positions for each minimized panel
  const panelPositions = useMemo(() => {
    if (!width || !height || minimizedPanels.length === 0) {
      return {};
    }

    const positions: Record<string, { right: number; bottom: number }> = {};
    let currentRight = MINIMIZED_PANEL_MARGIN;

    // Calculate positions from right to left (last panel alphabetically on the right)
    for (let i = minimizedPanels.length - 1; i >= 0; i--) {
      const panelId = minimizedPanels[i];
      const panelWidth = panelWidths[panelId] || 150; // Default width if not measured yet
      
      positions[panelId] = {
        right: currentRight,
        bottom: MINIMIZED_PANEL_MARGIN,
      };

      currentRight += panelWidth + MINIMIZED_PANEL_GAP;
    }

    return positions;
  }, [minimizedPanels, width, height, panelWidths]);

  // Measure panel widths when they mount
  const handlePanelRef = (panelId: string, element: HTMLDivElement | null) => {
    if (element) {
      const measuredWidth = element.offsetWidth;
      setPanelWidths(prev => {
        if (prev[panelId] !== measuredWidth) {
          return { ...prev, [panelId]: measuredWidth };
        }
        return prev;
      });
    }
  };

  if (minimizedPanels.length === 0) {
    return null;
  }

  return (
    <div className={styles.Container}>
      {minimizedPanels.map((panelId) => {
        const position = panelPositions[panelId];
        const expandHandler = getExpandHandler(panelId);
        const title = getPanelTitle(panelId);

        if (!expandHandler || !title || !position) {
          return null;
        }

        return (
          <div
            key={panelId}
            ref={(el) => handlePanelRef(panelId, el)}
            className={styles.PanelWrapper}
            style={{
              right: `${position.right}px`,
              bottom: `${position.bottom}px`,
            }}
          >
            <MinimizedPanel
              title={title}
              onExpand={expandHandler}
              panelId={panelId}
            />
          </div>
        );
      })}
    </div>
  );
};

export default MinimizedPanelsContainer;

